/*
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use
 * this file except in compliance with the License. You may obtain a copy of the
 * License at
 *
 *  https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed
 * under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations under the License.
 */
import { useEffect, useState } from "react";
import { noop } from "lodash";
import { IResponseMany } from "../interfaces";

/**
 * Custom hook that:
 * - Monitors only the initial page of a paginated request
 * - Extracts the total number of entities available (our api returns a valid value only for the initial page)
 * - If a callback function is provided, call this function with the new payload
 *
 * `selectTotal` lets a caller read an endpoint-specific count off the
 * first-page meta (latched here, so later pages can't change the value);
 * defaults to the standard `meta.results`.
 */
export function useInitialPaginationFetch(
  search: any,
  swrResponse: IResponseMany,
  callback: (params: IResponseMany | null) => void = noop,
  selectTotal: (meta: any) => number = (meta) => meta?.results
) {
  const [totalResults, setTotalResults] = useState(null);

  /**
   * Hook up search changes with resetting the total number of results.
   * Also, if a callback is provided, trigger the callback reset event
   * */
  useEffect(() => {
    setTotalResults(null);
    callback(null);
    swrResponse?.resetPage && swrResponse?.resetPage();
  }, [search]);

  /**
   * Hook that updates the total number of results only when requesting
   * for the first page of a paginated request
   * */
  useEffect(() => {
    // == null (not falsy): 0 is a valid latched total — a falsy check would
    // re-read later pages' merged meta, which only carries first-page fields.
    if (totalResults == null || swrResponse.size === 1) {
      setTotalResults(selectTotal(swrResponse?.meta));
      callback(swrResponse);
    }
  }, [swrResponse?.meta, swrResponse?.size, totalResults]);

  return {
    totalResults,
  };
}
